import React, { useState } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { Download, AlertCircle, Loader2 } from 'lucide-react';
import { useCompanyBranding } from '@/hooks/useCompanyBranding';
import { AnalysisPDFDocument } from './AnalysisPDFDocument';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';

interface PDFDownloadButtonProps {
  dpeData: any;
  sessionData?: any;
  confidenceScore: number;
  rulesAppliedCount?: number;
  inconsistenciesDetected: any[];
  immediateActions?: any[];
  shortTermActions?: any[];
  hasCollectiveSystem?: boolean;
  collectiveSystemWarning?: any;
  analysisDate?: string;
  variant?: 'primary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  showAsTooltip?: boolean;
  onDownloadStart?: () => void;
  onDownloadComplete?: () => void;
  onDownloadError?: (error: Error) => void;
}

export const PDFDownloadButton: React.FC<PDFDownloadButtonProps> = ({
  dpeData,
  sessionData,
  confidenceScore,
  rulesAppliedCount,
  inconsistenciesDetected,
  immediateActions,
  shortTermActions,
  hasCollectiveSystem,
  collectiveSystemWarning,
  analysisDate,
  variant = 'primary',
  size = 'md',
  showAsTooltip = false,
  onDownloadStart,
  onDownloadComplete,
  onDownloadError,
}) => {
  const { companyName, companyLogoUrl, primaryColor, isProUser, loading: brandingLoading } = useCompanyBranding();
  const [error, setError] = useState<string | null>(null);

  const generateFileName = () => {
    const address = dpeData?.adresse_ban || 'analyse';
    const cleanAddress = address
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 50);

    const date = new Date().toISOString().split('T')[0];
    return `rapport-dpe-${cleanAddress}-${date}.pdf`;
  };

  const DPEWIZARD_LOGO_URL = 'https://gitjbvjcespvbktehxcn.supabase.co/storage/v1/object/public/company-logos/default/dpewizard-logo.png';
  const effectiveCompanyName = isProUser ? companyName : 'DPEWizard';
  const effectiveLogoUrl = isProUser ? companyLogoUrl : DPEWIZARD_LOGO_URL;
  const effectivePrimaryColor = isProUser ? primaryColor : undefined;

  if (brandingLoading) {
    return (
      <Button variant={variant} size={size} disabled>
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Chargement...
      </Button>
    );
  }

  const proUpgradeMessage = (
    <div className="text-left">
      <p className="text-sm mb-2">
        Passez à <strong>DPEWizard Pro</strong> pour personnaliser vos rapports PDF avec votre logo et nom d'entreprise
      </p>
      <a
        href="/pricing"
        className="text-sm font-medium text-blue-300 hover:text-blue-100 underline"
      >
        Découvrir Pro →
      </a>
    </div>
  );

  const pdfButton = (
    <>
      {error && (
        <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-900">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-sm font-medium text-red-600 hover:text-red-700 underline mt-1"
            >
              Réessayer
            </button>
          </div>
        </div>
      )}

      <PDFDownloadLink
        document={
          <AnalysisPDFDocument
            companyName={effectiveCompanyName}
            logoUrl={effectiveLogoUrl}
            primaryColor={effectivePrimaryColor}
            dpeData={dpeData}
            sessionData={sessionData}
            confidenceScore={confidenceScore}
            rulesAppliedCount={rulesAppliedCount}
            inconsistenciesDetected={inconsistenciesDetected}
            immediateActions={immediateActions}
            shortTermActions={shortTermActions}
            hasCollectiveSystem={hasCollectiveSystem}
            collectiveSystemWarning={collectiveSystemWarning}
            analysisDate={analysisDate}
          />
        }
        fileName={generateFileName()}
        className="inline-block"
      >
        {({ blob, url, loading: pdfLoading, error: pdfError }) => {
          if (pdfError) {
            if (!error) {
              setError('Erreur lors de la génération du PDF. Veuillez réessayer.');
              onDownloadError?.(pdfError);
            }
          }

          if (pdfLoading) {
            onDownloadStart?.();
          } else if (blob && url && !pdfError) {
            onDownloadComplete?.();
          }

          return (
            <Button
              variant={variant}
              size={size}
              disabled={pdfLoading}
            >
              {pdfLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Génération du PDF...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Télécharger le rapport
                </>
              )}
            </Button>
          );
        }}
      </PDFDownloadLink>
    </>
  );

  if (showAsTooltip && !isProUser) {
    return (
      <Tooltip content={proUpgradeMessage} side="bottom" maxWidth="350px">
        {pdfButton}
      </Tooltip>
    );
  }

  return pdfButton;
};
